import { ArrowLeft, Building2, Calendar, Gauge, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { TenantMembersTable } from '../components/TenantMembersTable';
import { TenantPlanSection } from '../components/TenantPlanSection';
import { TenantResourceUsage } from '../components/TenantResourceUsage';
import { useTenantDetail } from '../hooks/useTenantDetail';
import { useTenantMembers } from '../hooks/useTenantMembers';
import { getTenantStatusInfo } from '../utils/tenant-status.utils';
import { privatePaths } from '@/app/router/paths/privatePaths';
import { InfoRow } from '@/shared/components/ui/info-row';
import { PlaceholderSection } from '@/shared/components/ui/placeholder-section';
import { SectionCard } from '@/shared/components/ui/section-card';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatIsoDate } from '@/shared/utils/format-date.utils';

export default function TenantDetailPage() {
  const { id: tenantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [membersPage, setMembersPage] = useState(1);

  const { tenant } = useTenantDetail(tenantId ?? '');
  const {
    members,
    pagination: membersPagination,
    isLoading: membersLoading,
  } = useTenantMembers(tenantId ?? '', { page: membersPage, pageSize: 10 });

  if (!tenantId) {
    navigate(privatePaths.TENANTS.home);
    return null;
  }

  const statusInfo = tenant ? getTenantStatusInfo(tenant.status) : null;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <UnifiedButton
          variant="outline"
          onClick={() => navigate(privatePaths.TENANTS.home)}
          className="shrink-0"
        >
          <ArrowLeft size={16} />
        </UnifiedButton>
        <div className="flex items-center gap-3 min-w-0">
          <Building2 size={16} className="text-primary shrink-0" />
          <div className="flex flex-col min-w-0">
            <h1 className="text-light text-lg font-bold truncate">{tenant?.name ?? tenantId}</h1>
            {tenant && (
              <span className="text-primary text-xs font-mono opacity-60">{tenant.slug}</span>
            )}
          </div>
          {statusInfo && <StatusBadge status={statusInfo.variant} label={statusInfo.label} />}
        </div>
      </div>

      {/* Información general */}
      <SectionCard title="Información general" icon={<Building2 size={16} />}>
        {tenant ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <InfoRow label="Propietario" value={tenant.ownerName ?? '—'} />
            <InfoRow label="País base" value={tenant.countryCode ?? '—'} />
            <InfoRow label="Estado suscripción" value={statusInfo?.label ?? tenant.status} />
            <InfoRow label="Máx. usuarios" value={`${tenant.maxUsers} usuarios`} />
            <InfoRow label="Miembros activos" value={tenant.memberCount} />
            <InfoRow label="Fecha de creación" value={formatIsoDate(tenant.createdAt, 'long')} />
            <InfoRow label="Última actualización" value={formatIsoDate(tenant.updatedAt, 'long')} />
            <InfoRow label="Validado hasta" value={formatIsoDate(tenant.trialEndsAt, 'long')} />
          </div>
        ) : (
          <PlaceholderSection message="No se encontró información del espacio de trabajo." />
        )}
      </SectionCard>

      {/* Miembros y roles */}
      <SectionCard title="Miembros y roles" icon={<Users size={16} />}>
        {membersLoading ? (
          <div className="py-8" aria-hidden />
        ) : (
          <TenantMembersTable
            data={members}
            pagination={membersPagination}
            onPageChange={setMembersPage}
            onPageSizeChange={() => {}}
            emptyMessage="Este espacio de trabajo no tiene miembros."
          />
        )}
      </SectionCard>

      {/* Plan y suscripción */}
      <SectionCard title="Plan y suscripción" icon={<Calendar size={16} />}>
        {tenant ? (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex flex-col gap-1 p-4 rounded-xl border border-outline">
                <span className="text-primary text-xs opacity-60 uppercase tracking-wider">
                  Estado
                </span>
                <span className="text-light text-sm font-medium">
                  {statusInfo?.label ?? tenant.status}
                </span>
              </div>
              <div className="flex flex-col gap-1 p-4 rounded-xl border border-outline">
                <span className="text-primary text-xs opacity-60 uppercase tracking-wider">
                  Plan
                </span>
                <span className="text-light text-sm font-medium">{tenant.tierName}</span>
              </div>
              <div className="flex flex-col gap-1 p-4 rounded-xl border border-outline">
                <span className="text-primary text-xs opacity-60 uppercase tracking-wider">
                  Válido hasta
                </span>
                <span className="text-light text-sm font-medium">
                  {tenant.trialEndsAt ? formatIsoDate(tenant.trialEndsAt, 'long') : '—'}
                </span>
              </div>
            </div>
            <TenantPlanSection tenant={tenant} />
          </div>
        ) : (
          <PlaceholderSection message="No disponible." />
        )}
      </SectionCard>

      {/* Recursos del plan */}
      {tenant && (
        <SectionCard title="Recursos del plan" icon={<Gauge size={16} />}>
          <TenantResourceUsage tenant={tenant} />
        </SectionCard>
      )}
    </div>
  );
}
